import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from '@/api/employees';
import { useAuth } from '@/contexts/AuthContext';
import type { Employee } from '@/types/resources/employee';

interface EmployeeSelectorProps {
  disabled?: boolean;
  fieldName?: string;
  onEmployeeDataChange?: (employee: { id: string; name: string } | null) => void;
}

export const EmployeeSelector: React.FC<EmployeeSelectorProps> = ({
  disabled = false,
  fieldName = 'employeeId',
  onEmployeeDataChange
}) => {
  const { t } = useTranslation(['employees', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch employees
  const { data: employeesData, error } = useQuery({
    queryKey: ['employees', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getEmployees({ 
        firestoreUser,
        pageSize: 100 // Get all employees for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Initialize employee data from form values when component mounts or form values change
  useEffect(() => {
    const employeeId = form.getFieldValue(fieldName);
    if (employeeId && employeesData?.employees) {
      const employee = employeesData.employees.find((e: Employee) => e.id === employeeId);
      onEmployeeDataChange?.(employee ? { 
        id: employee.id, 
        name: employee.user.displayName 
      } : null);
    }
  }, [form, employeesData, onEmployeeDataChange, fieldName]);

  const handleEmployeeChange = (employeeId: string) => {
    // Provide employee data to parent
    if (employeeId) {
      const employee = employeesData?.employees?.find((e: Employee) => e.id === employeeId);
      onEmployeeDataChange?.(employee ? { 
        id: employee.id, 
        name: employee.user.displayName 
      } : null);
    } else {
      onEmployeeDataChange?.(null);
    }
  };

  const employeeOptions = employeesData?.employees?.map((employee: Employee) => ({
    value: employee.id,
    label: employee.user.displayName,
  })) || [];

  // Show error state if API fails
  if (error) {
    return (
      <Form.Item
        name={fieldName}
        label={t('employees:fields.name')}
      >
        <Select
          placeholder={t('employees:fields.selectEmployee')}
          disabled={true}
          options={[]}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={fieldName}
      label={t('employees:fields.name')}
    >
      <Select
        placeholder={t('employees:fields.selectEmployee')}
        onChange={handleEmployeeChange}
        disabled={disabled}
        allowClear
        options={employeeOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
}; 